import { SettingsType } from "building-types/settings/settingsType";
import { WindowMullionType } from "building-types/settings/settingsTypesEnum";
import type { WindowCatalogSettings } from "building-types/settings/windowCatalogSettings";
import {
  GlazingType,
  WindowOperationType,
  WindowPanelType,
  WindowVertDivisions,
} from "building-types/settings/windowTypesEnum";
import { BuiltinWindowId } from "building-types/windows/windowId";
import type { WindowDescriptor } from "building-types/windows/windowTypes";
import type { Table } from "common/data/table";
import { levelInchesNew } from "common/utils/nominalNumbers";
import { getDescriptorName } from "../../catalogType";
import { windowDescriptorNew } from "./windowDescriptors";

export function getWindowDescriptorName(descriptor: WindowDescriptor): string | undefined {
  return getDescriptorName(descriptor, BuiltinWindowId);
}

export const defaultWindowCatalog: { [type in BuiltinWindowId]: WindowDescriptor } = {
  A1840: windowDescriptorNew({
    id: BuiltinWindowId.A1840,
    width: levelInchesNew(20),
    height: levelInchesNew(48),
    reportedName: "18/40",
  }),
  A2040: windowDescriptorNew({
    id: BuiltinWindowId.A2040,
    width: levelInchesNew(24),
    height: levelInchesNew(48),
    reportedName: "20/40",
  }),
  A3030: windowDescriptorNew({
    id: BuiltinWindowId.A3030,
    width: levelInchesNew(36),
    height: levelInchesNew(36),
    reportedName: "30/30",
  }),
  A4040: windowDescriptorNew({
    id: BuiltinWindowId.A4040,
    width: levelInchesNew(48),
    height: levelInchesNew(48),
    reportedName: "40/40",
  }),
  A2046: windowDescriptorNew({
    id: BuiltinWindowId.A2046,
    width: levelInchesNew(24),
    height: levelInchesNew(54),
    reportedName: "20/46",
  }),
  A2640: windowDescriptorNew({
    id: BuiltinWindowId.A2640,
    width: levelInchesNew(30),
    height: levelInchesNew(48),
    reportedName: "26/40",
  }),
  A71046: windowDescriptorNew({
    id: BuiltinWindowId.A71046,
    width: levelInchesNew(94),
    height: levelInchesNew(54),
    reportedName: "710/46",
  }),
  A2030: windowDescriptorNew({
    id: BuiltinWindowId.A2050,
    width: levelInchesNew(24),
    height: levelInchesNew(36),
    reportedName: "20/30",
  }),
  A2080: windowDescriptorNew({
    id: BuiltinWindowId.A2080,
    width: levelInchesNew(24),
    height: levelInchesNew(96),
    reportedName: "20/80",
  }),
  A2050: windowDescriptorNew({
    id: BuiltinWindowId.A2050,
    width: levelInchesNew(24),
    height: levelInchesNew(60),
    reportedName: "20/50",
  }),
  A2060: windowDescriptorNew({
    id: BuiltinWindowId.A2060,
    width: levelInchesNew(24),
    height: levelInchesNew(72),
    reportedName: "20/60",
  }),
  A2550: windowDescriptorNew({
    id: BuiltinWindowId.A2550,
    width: levelInchesNew(29),
    height: levelInchesNew(60),
    reportedName: "25/50",
  }),
  A2650: windowDescriptorNew({
    id: BuiltinWindowId.A2650,
    width: levelInchesNew(30),
    height: levelInchesNew(60),
    reportedName: "26/50",
  }),
  A3040: windowDescriptorNew({
    id: BuiltinWindowId.A3040,
    width: levelInchesNew(36),
    height: levelInchesNew(48),
    reportedName: "30/40",
  }),
  A3040_TEMP: windowDescriptorNew({
    id: BuiltinWindowId.A3040_TEMP,
    width: levelInchesNew(36),
    height: levelInchesNew(48),
    reportedName: "30/40",
    materials: { glazingType: GlazingType.TEMPERED },
  }),
  A3046: windowDescriptorNew({
    id: BuiltinWindowId.A3046,
    width: levelInchesNew(36),
    height: levelInchesNew(54),
    reportedName: "30/46",
    materials: { glazingType: GlazingType.STANDARD },
  }),
  A3050: windowDescriptorNew({
    id: BuiltinWindowId.A3050,
    width: levelInchesNew(36),
    height: levelInchesNew(60),
    reportedName: "30/50",
  }),
  A3050_BRICK: windowDescriptorNew({
    id: BuiltinWindowId.A3050_BRICK,
    width: levelInchesNew(36),
    height: levelInchesNew(60),
    reportedName: "30/50",
  }),
  A3050_TEMP: windowDescriptorNew({
    id: BuiltinWindowId.A3050_TEMP,
    width: levelInchesNew(36),
    height: levelInchesNew(60),
    reportedName: "30/50",
    materials: { glazingType: GlazingType.TEMPERED },
  }),
  A3850: windowDescriptorNew({
    id: BuiltinWindowId.A3850,
    width: levelInchesNew(44),
    height: levelInchesNew(60),
    reportedName: "38/50",
  }),
  A4050: windowDescriptorNew({
    id: BuiltinWindowId.A4050,
    width: levelInchesNew(48),
    height: levelInchesNew(60),
    reportedName: "40/50",
  }),
  A5050: windowDescriptorNew({
    id: BuiltinWindowId.A5050,
    width: levelInchesNew(60),
    height: levelInchesNew(60),
    reportedName: "50/50",
  }),
  A2860: windowDescriptorNew({
    id: BuiltinWindowId.A2860,
    width: levelInchesNew(32),
    height: levelInchesNew(72),
    reportedName: "28/60",
  }),
  A3060: windowDescriptorNew({
    id: BuiltinWindowId.A3060,
    width: levelInchesNew(36),
    height: levelInchesNew(72),
    reportedName: "30/60",
  }),
  A4060: windowDescriptorNew({
    id: BuiltinWindowId.A4060,
    width: levelInchesNew(48),
    height: levelInchesNew(72),
    reportedName: "40/60",
  }),
  A5060: windowDescriptorNew({
    id: BuiltinWindowId.A5060,
    width: levelInchesNew(60),
    height: levelInchesNew(72),
    reportedName: "50/60",
  }),
  A31080: windowDescriptorNew({
    id: BuiltinWindowId.A31080,
    width: levelInchesNew(46),
    height: levelInchesNew(96),
    reportedName: "310/80",
    mullions: { type: SettingsType.WINDOW_MULLION_SETTINGS, mullionType: WindowMullionType.DISABLED },
  }),
  A4080: windowDescriptorNew({
    id: BuiltinWindowId.A4080,
    width: levelInchesNew(48),
    height: levelInchesNew(96),
    reportedName: "40/80",
    mullions: { type: SettingsType.WINDOW_MULLION_SETTINGS, mullionType: WindowMullionType.DISABLED },
  }),
  A5080: windowDescriptorNew({
    id: BuiltinWindowId.A5080,
    width: levelInchesNew(60),
    height: levelInchesNew(96),
    reportedName: "50/80",
    mullions: { type: SettingsType.WINDOW_MULLION_SETTINGS, mullionType: WindowMullionType.DISABLED },
  }),
  A31090: windowDescriptorNew({
    id: BuiltinWindowId.A31090,
    width: levelInchesNew(46),
    height: levelInchesNew(108),
    reportedName: "310/90",
    mullions: { type: SettingsType.WINDOW_MULLION_SETTINGS, mullionType: WindowMullionType.DISABLED },
  }),
  A4090: windowDescriptorNew({
    id: BuiltinWindowId.A4090,
    width: levelInchesNew(48),
    height: levelInchesNew(108),
    reportedName: "40/90",
    mullions: { type: SettingsType.WINDOW_MULLION_SETTINGS, mullionType: WindowMullionType.DISABLED },
  }),
  A5090: windowDescriptorNew({
    id: BuiltinWindowId.A5090,
    width: levelInchesNew(60),
    height: levelInchesNew(108),
    reportedName: "50/90",
    mullions: { type: SettingsType.WINDOW_MULLION_SETTINGS, mullionType: WindowMullionType.DISABLED },
  }),
  TW2636: windowDescriptorNew({
    id: BuiltinWindowId.TW2636,
    width: levelInchesNew(60),
    height: levelInchesNew(42),
    verticalDivisions: WindowVertDivisions.TWIN,
    reportedName: "26/36",
  }),
  TW2640: windowDescriptorNew({
    id: BuiltinWindowId.TW2640,
    width: levelInchesNew(60),
    height: levelInchesNew(48),
    verticalDivisions: WindowVertDivisions.TWIN,
    reportedName: "26/40",
  }),
  TW2646: windowDescriptorNew({
    id: BuiltinWindowId.TW2646,
    width: levelInchesNew(60),
    height: levelInchesNew(54),
    verticalDivisions: WindowVertDivisions.TWIN,
    reportedName: "26/46",
    materials: { glazingType: GlazingType.STANDARD },
  }),
  TW2650: windowDescriptorNew({
    id: BuiltinWindowId.TW2650,
    width: levelInchesNew(60),
    height: levelInchesNew(60),
    verticalDivisions: WindowVertDivisions.TWIN,
    reportedName: "26/50",
  }),
  TW3020: windowDescriptorNew({
    id: BuiltinWindowId.TW3020,
    width: levelInchesNew(72),
    height: levelInchesNew(24),
    verticalDivisions: WindowVertDivisions.TWIN,
    reportedName: "30/20",
  }),
  TW3050: windowDescriptorNew({
    id: BuiltinWindowId.TW3050,
    width: levelInchesNew(72),
    height: levelInchesNew(60),
    verticalDivisions: WindowVertDivisions.TWIN,
    reportedName: "30/50",
  }),
  TW3050_TEMP_HALF: windowDescriptorNew({
    id: BuiltinWindowId.TW3050_TEMP_HALF,
    width: levelInchesNew(72),
    height: levelInchesNew(60),
    verticalDivisions: WindowVertDivisions.TWIN,
    reportedName: "30/50",
    materials: { glazingType: GlazingType.TEMPERED },
  }),
  TW3060: windowDescriptorNew({
    id: BuiltinWindowId.TW3060,
    width: levelInchesNew(72),
    height: levelInchesNew(72),
    verticalDivisions: WindowVertDivisions.TWIN,
    reportedName: "30/60",
  }),
  TW3080: windowDescriptorNew({
    id: BuiltinWindowId.TW3080,
    width: levelInchesNew(72),
    height: levelInchesNew(96),
    verticalDivisions: WindowVertDivisions.TWIN,
    reportedName: "30/80",
  }),
  TW4080: windowDescriptorNew({
    id: BuiltinWindowId.TW4080,
    width: levelInchesNew(96),
    height: levelInchesNew(96),
    verticalDivisions: WindowVertDivisions.TWIN,
    reportedName: "40/80",
  }),
  TR2640: windowDescriptorNew({
    id: BuiltinWindowId.TR2640,
    width: levelInchesNew(90),
    height: levelInchesNew(48),
    verticalDivisions: WindowVertDivisions.TRIPLE,
    reportedName: "26/40",
  }),
  TR2650: windowDescriptorNew({
    id: BuiltinWindowId.TR2650,
    width: levelInchesNew(90),
    height: levelInchesNew(60),
    verticalDivisions: WindowVertDivisions.TRIPLE,
    reportedName: "26/50",
  }),
  TR2660: windowDescriptorNew({
    id: BuiltinWindowId.TR2660,
    width: levelInchesNew(90),
    height: levelInchesNew(72),
    verticalDivisions: WindowVertDivisions.TRIPLE,
    reportedName: "26/60",
  }),
  TR3050: windowDescriptorNew({
    id: BuiltinWindowId.TR3050,
    width: levelInchesNew(108),
    height: levelInchesNew(60),
    verticalDivisions: WindowVertDivisions.TRIPLE,
    reportedName: "30/50",
  }),
  TR3060: windowDescriptorNew({
    id: BuiltinWindowId.TR3060,
    width: levelInchesNew(108),
    height: levelInchesNew(72),
    verticalDivisions: WindowVertDivisions.TRIPLE,
    reportedName: "30/60",
  }),
  TR2050: windowDescriptorNew({
    id: BuiltinWindowId.TR2050,
    width: levelInchesNew(60),
    height: levelInchesNew(60),
    verticalDivisions: WindowVertDivisions.TRIPLE,
    reportedName: "20/50",
  }),
  TR2030: windowDescriptorNew({
    id: BuiltinWindowId.TR2030,
    width: levelInchesNew(72),
    height: levelInchesNew(36),
    verticalDivisions: WindowVertDivisions.TRIPLE,
    reportedName: "20/30",
  }),
  TR2020: windowDescriptorNew({
    id: BuiltinWindowId.TR2020,
    width: levelInchesNew(72),
    height: levelInchesNew(24),
    verticalDivisions: WindowVertDivisions.TRIPLE,
    reportedName: "20/20",
    mullions: { type: SettingsType.WINDOW_MULLION_SETTINGS, mullionType: WindowMullionType.DISABLED },
  }),
  TR3080: windowDescriptorNew({
    id: BuiltinWindowId.TR3080,
    width: levelInchesNew(108),
    height: levelInchesNew(96),
    verticalDivisions: WindowVertDivisions.TRIPLE,
    reportedName: "30/80",
  }),
  TR13880: windowDescriptorNew({
    id: BuiltinWindowId.TR13880,
    width: levelInchesNew(138),
    height: levelInchesNew(96),
    verticalDivisions: WindowVertDivisions.TRIPLE,
    reportedName: "310/80",
    mullions: { type: SettingsType.WINDOW_MULLION_SETTINGS, mullionType: WindowMullionType.DISABLED },
  }),
  TR13890: windowDescriptorNew({
    id: BuiltinWindowId.TR13890,
    width: levelInchesNew(138),
    height: levelInchesNew(108),
    verticalDivisions: WindowVertDivisions.TRIPLE,
    reportedName: "310/90",
    mullions: { type: SettingsType.WINDOW_MULLION_SETTINGS, mullionType: WindowMullionType.DISABLED },
  }),
  T1: windowDescriptorNew({
    id: BuiltinWindowId.T1,
    width: levelInchesNew(60),
    height: levelInchesNew(56),
    verticalDivisions: WindowVertDivisions.TWIN,
    reportedName: "50/48",
  }),
  T2: windowDescriptorNew({
    id: BuiltinWindowId.T2,
    width: levelInchesNew(36),
    height: levelInchesNew(12),
    reportedName: "30/10",
  }),
  T2620: windowDescriptorNew({
    id: BuiltinWindowId.T2620,
    width: levelInchesNew(30),
    height: levelInchesNew(24),
    reportedName: "26/20",
    mullions: { type: SettingsType.WINDOW_MULLION_SETTINGS, mullionType: WindowMullionType.DISABLED },
    operationType: WindowOperationType.FIXED,
  }),
  T3620: windowDescriptorNew({
    id: BuiltinWindowId.T3620,
    width: levelInchesNew(42),
    height: levelInchesNew(24),
    reportedName: "36/20",
    mullions: { type: SettingsType.WINDOW_MULLION_SETTINGS, mullionType: WindowMullionType.DISABLED },
    operationType: WindowOperationType.FIXED,
  }),
  T3010: windowDescriptorNew({
    id: BuiltinWindowId.T3010,
    width: levelInchesNew(36),
    height: levelInchesNew(12),
    reportedName: "30/10",
    mullions: { type: SettingsType.WINDOW_MULLION_SETTINGS, mullionType: WindowMullionType.DISABLED },
    operationType: WindowOperationType.FIXED,
  }),
  T2010: windowDescriptorNew({
    id: BuiltinWindowId.T2010,
    width: levelInchesNew(24),
    height: levelInchesNew(12),
    reportedName: "20/10",
    mullions: { type: SettingsType.WINDOW_MULLION_SETTINGS, mullionType: WindowMullionType.DISABLED },
    operationType: WindowOperationType.FIXED,
  }),
  T4010: windowDescriptorNew({
    id: BuiltinWindowId.T4010,
    width: levelInchesNew(48),
    height: levelInchesNew(12),
    reportedName: "40/10",
    mullions: { type: SettingsType.WINDOW_MULLION_SETTINGS, mullionType: WindowMullionType.DISABLED },
    operationType: WindowOperationType.FIXED,
  }),
  T5010: windowDescriptorNew({
    id: BuiltinWindowId.T5010,
    width: levelInchesNew(60),
    height: levelInchesNew(12),
    reportedName: "50/10",
    mullions: { type: SettingsType.WINDOW_MULLION_SETTINGS, mullionType: WindowMullionType.DISABLED },
    operationType: WindowOperationType.FIXED,
  }),
  T6010: windowDescriptorNew({
    id: BuiltinWindowId.T6010,
    width: levelInchesNew(72),
    height: levelInchesNew(12),
    reportedName: "60/10",
    mullions: { type: SettingsType.WINDOW_MULLION_SETTINGS, mullionType: WindowMullionType.DISABLED },
    operationType: WindowOperationType.FIXED,
  }),
  T6020: windowDescriptorNew({
    id: BuiltinWindowId.T6020,
    width: levelInchesNew(72),
    height: levelInchesNew(24),
    reportedName: "60/20",
    mullions: { type: SettingsType.WINDOW_MULLION_SETTINGS, mullionType: WindowMullionType.DISABLED },
    operationType: WindowOperationType.FIXED,
  }),
  T8010: windowDescriptorNew({
    id: BuiltinWindowId.T8010,
    width: levelInchesNew(96),
    height: levelInchesNew(12),
    reportedName: "80/10",
    mullions: { type: SettingsType.WINDOW_MULLION_SETTINGS, mullionType: WindowMullionType.DISABLED },
    operationType: WindowOperationType.FIXED,
  }),
  T2020: windowDescriptorNew({
    id: BuiltinWindowId.T2020,
    width: levelInchesNew(24),
    height: levelInchesNew(24),
    reportedName: "20/20",
    mullions: { type: SettingsType.WINDOW_MULLION_SETTINGS, mullionType: WindowMullionType.DISABLED },
    operationType: WindowOperationType.FIXED,
  }),
  T2020_LOW: windowDescriptorNew({
    id: BuiltinWindowId.T2020_LOW,
    width: levelInchesNew(24),
    height: levelInchesNew(24),
    reportedName: "20/20",
    operationType: WindowOperationType.FIXED,
  }),
  TW2220: windowDescriptorNew({
    id: BuiltinWindowId.TW2220,
    width: levelInchesNew(52),
    height: levelInchesNew(24),
    verticalDivisions: WindowVertDivisions.TWIN,
    reportedName: "44/20",
  }),
  TW2040: windowDescriptorNew({
    id: BuiltinWindowId.TW2040,
    width: levelInchesNew(48),
    height: levelInchesNew(48),
    verticalDivisions: WindowVertDivisions.TWIN,
    reportedName: "20/40",
  }),
  G1620: windowDescriptorNew({
    id: BuiltinWindowId.G1620,
    width: levelInchesNew(18),
    height: levelInchesNew(24),
    reportedName: "16/20",
    mullions: { type: SettingsType.WINDOW_MULLION_SETTINGS, mullionType: WindowMullionType.DISABLED },
    materials: { glazingType: GlazingType.STANDARD },
    operationType: WindowOperationType.FIXED,
  }),
  G2020: windowDescriptorNew({
    id: BuiltinWindowId.G2020,
    width: levelInchesNew(24),
    height: levelInchesNew(24),
    reportedName: "20/20",
    mullions: { type: SettingsType.WINDOW_MULLION_SETTINGS, mullionType: WindowMullionType.DISABLED },
    materials: { glazingType: GlazingType.STANDARD },
    operationType: WindowOperationType.FIXED,
  }),
  G2030: windowDescriptorNew({
    id: BuiltinWindowId.G2030,
    width: levelInchesNew(24),
    height: levelInchesNew(36),
    reportedName: "20/30",
    materials: { glazingType: GlazingType.STANDARD },
  }),
  G2436: windowDescriptorNew({
    id: BuiltinWindowId.G2436,
    width: levelInchesNew(28),
    height: levelInchesNew(42),
    reportedName: "24/36",
    materials: { glazingType: GlazingType.STANDARD },
  }),
  G2040: windowDescriptorNew({
    id: BuiltinWindowId.G2040,
    width: levelInchesNew(24),
    height: levelInchesNew(48),
    reportedName: "20/40",
    materials: { glazingType: GlazingType.STANDARD },
  }),
  G3040: windowDescriptorNew({
    id: BuiltinWindowId.G3040,
    width: levelInchesNew(32),
    height: levelInchesNew(42),
    reportedName: "20/20",
    materials: { glazingType: GlazingType.STANDARD },
  }),
  G6630: windowDescriptorNew({
    id: BuiltinWindowId.G6630,
    width: levelInchesNew(88),
    height: levelInchesNew(36),
    verticalDivisions: WindowVertDivisions.TRIPLE,
    reportedName: "66/30",
    materials: { glazingType: GlazingType.STANDARD },
  }),
  S1: windowDescriptorNew({
    id: BuiltinWindowId.S1,
    width: levelInchesNew(42),
    height: levelInchesNew(68),
    reportedName: "20/20",
  }),
  S2: windowDescriptorNew({
    id: BuiltinWindowId.S2,
    width: levelInchesNew(64),
    height: levelInchesNew(72),
    verticalDivisions: WindowVertDivisions.TWIN,
    reportedName: "20/20",
  }),
  SL1: windowDescriptorNew({
    id: BuiltinWindowId.SL1,
    width: levelInchesNew(12),
    height: levelInchesNew(92.5),
    reportedName: "20/20",
    mullions: { type: SettingsType.WINDOW_MULLION_SETTINGS, mullionType: WindowMullionType.DISABLED },
  }),
  D6020: windowDescriptorNew({
    id: BuiltinWindowId.D6020,
    width: levelInchesNew(72),
    height: levelInchesNew(24),
    verticalDivisions: WindowVertDivisions.TRIPLE,
    reportedName: "50/20",
  }),
  V2020: windowDescriptorNew({
    id: BuiltinWindowId.V2020,
    width: levelInchesNew(24),
    height: levelInchesNew(24),
    reportedName: "20/20",
  }),
  V0808: windowDescriptorNew({
    id: BuiltinWindowId.V0808,
    width: levelInchesNew(8),
    height: levelInchesNew(8),
    reportedName: "08/08",
  }),
  V1: windowDescriptorNew({
    id: BuiltinWindowId.V1,
    width: levelInchesNew(12),
    height: levelInchesNew(24),
    reportedName: "10/20",
  }),
  V1020: windowDescriptorNew({
    id: BuiltinWindowId.V1020,
    width: levelInchesNew(12),
    height: levelInchesNew(24),
    reportedName: "10/20",
    materials: { panelType: WindowPanelType.VENT },
  }),
  V0912: windowDescriptorNew({
    id: BuiltinWindowId.V0912,
    width: levelInchesNew(9),
    height: levelInchesNew(14),
    reportedName: "09/12",
    materials: { panelType: WindowPanelType.VENT },
  }),
  V1220: windowDescriptorNew({
    id: BuiltinWindowId.V1220,
    width: levelInchesNew(14),
    height: levelInchesNew(24),
    reportedName: "12/20",
    materials: { panelType: WindowPanelType.VENT },
  }),
  V1620: windowDescriptorNew({
    id: BuiltinWindowId.V1620,
    width: levelInchesNew(18),
    height: levelInchesNew(24),
    reportedName: "16/20",
    materials: { panelType: WindowPanelType.VENT },
  }),
  V1824: windowDescriptorNew({
    id: BuiltinWindowId.V1824,
    width: levelInchesNew(20),
    height: levelInchesNew(28),
    reportedName: "18/24",
    materials: { panelType: WindowPanelType.VENT },
  }),
  NO: windowDescriptorNew({
    id: BuiltinWindowId.NO,
    width: levelInchesNew(0),
    height: levelInchesNew(0),
    reportedName: "ERROR",
  }),
  K1: windowDescriptorNew({
    id: BuiltinWindowId.K1,
    width: levelInchesNew(24),
    height: levelInchesNew(48),
    reportedName: "20/40",
  }),
  D1: windowDescriptorNew({
    id: BuiltinWindowId.D1,
    width: levelInchesNew(30),
    height: levelInchesNew(48),
    reportedName: "26/40",
    materials: { glazingType: GlazingType.STANDARD },
    operationType: WindowOperationType.FIXED,
  }),
  FS3050: windowDescriptorNew({
    id: BuiltinWindowId.FS3050,
    width: levelInchesNew(36),
    height: levelInchesNew(60),
    materials: { panelType: WindowPanelType.FAUX_SHUTTER },
    reportedName: "SHUTTER",
  }),
};

export const defaultWindowTypes: Table<WindowDescriptor> = {
  "Default Catalog Window": windowDescriptorNew({
    id: "Default Catalog Window",
    name: "Default Catalog Window",
    width: levelInchesNew(36),
    height: levelInchesNew(60),
    reportedName: "30/50",
  }),
};

export function getWindowCatalog(windowCatalogSettings: WindowCatalogSettings): Table<WindowDescriptor> {
  const windowCatalog = windowCatalogSettings?.windowTypes ?? defaultWindowTypes;

  return { ...windowCatalog, ...defaultWindowCatalog };
}

export function getWindowCatalogsForUI(windowCatalogSettings: WindowCatalogSettings): Table<WindowDescriptor> {
  const windowCatalog = windowCatalogSettings?.windowTypes ?? defaultWindowTypes;

  if (windowCatalogSettings.displayBuiltInCatalog) {
    return { ...windowCatalog, ...defaultWindowCatalog };
  } else {
    return windowCatalog;
  }
}
